import React, {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react'

import clsx from 'clsx'

import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import {
  TaskButton,
  WarningButton,
  WarningCancelButton,
} from '$extensionSrc/components/Buttons'
import { AccessLevel } from '$extensionSrc/data/user/accessByPlatform'
import {
  BlueskySettingKey,
  FacebookSettingKey,
  LinkedinSettingKey,
  XSettingKey,
} from '$extensionSrc/recommendations/recommendationSettingsKeys'
import * as state from '$extensionSrc/state'
import { getTimeSinceTimestampStr, MONTHS } from '$extensionSrc/utils/dateUtils'
import {
  TwitterRecommendationKey,
  FBRecommendationKey,
  BlueskyRecommendationKey,
  LinkedinRecommendationKey,
  RecommendationState,
  PLATFORM,
} from '$extensionSrc/utils/enums'

import SelectDeleteTimeframe from './SelectDeleteTimeframe'
import SelectXPost from './SelectTwitterPost'
import styles from './TaskBody.module.scss'
import SettingRow from '../AutomatedBody/SettingRow'

export default function TaskBody({
  accessLevel,
  automationErrors,
  confirmTaskButtonText,
  continuingTaskButtonText,
  initialState,
  lastUpdatedTimestamp,
  locked,
  onConfirmClick,
  onClickUpsell,
  onClickCheckSettings,
  platform,
  platformUserId,
  recommendationKey,
  recommendationState,
  settings,
  settingsSelected,
  taskButtonText,
  taskContext,
  taskTitle,
  taskUpdatedText,
  taskWarningText,
}: {
  accessLevel: AccessLevel | undefined
  automationErrors: any
  confirmTaskButtonText: ReactNode
  continuingTaskButtonText: ReactNode
  initialState: any
  lastUpdatedTimestamp: number | null
  locked: boolean
  onConfirmClick: () => {}
  onClickUpsell: () => void
  onClickCheckSettings: () => void
  platform: PLATFORM
  platformUserId: string
  recommendationKey: string
  recommendationState: RecommendationState
  settings: { key: string; description: ReactNode }[]
  settingsSelected: any
  taskButtonText: ReactNode
  taskContext: string[]
  taskTitle: ReactNode
  taskUpdatedText: ReactNode
  taskWarningText: ReactNode
}) {
  // Tasks use a designated setting key to store selected date
  // if they use a date option
  const settingKey = useMemo(() => {
    if (platform === PLATFORM.BLUESKY) {
      return BlueskySettingKey.DELETE_BLUESKY_POSTS_BEFORE
    } else if (platform === PLATFORM.TWITTER) {
      return XSettingKey.DELETE_POSTS_BEFORE
    } else if (platform === PLATFORM.FACEBOOK) {
      switch (recommendationKey) {
        case FBRecommendationKey.FB_DELETE_POSTS:
          return FacebookSettingKey.DELETE_POSTS_PHOTOS_VIDEOS
        case FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES:
          return FacebookSettingKey.DELETE_COMMENTS_FB
        case FBRecommendationKey.FB_DELETE_PAGE_LIKES:
          return FacebookSettingKey.DELETE_PAGE_LIKES
        default:
          return null
      }
    } else if (platform === PLATFORM.LINKEDIN) {
      switch (recommendationKey) {
        case LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE:
          return LinkedinSettingKey.DELETE_LINKEDIN_POSTS_BEFORE
        default:
          return null
      }
    }

    return null
  }, [platform, recommendationKey])

  const [taskIsEnabled, setTaskIsEnabled] = useState<boolean>(false)
  const [buttonHelperText, setButtonHelperText] = useState<string | undefined>(
    undefined,
  )

  const [cardFooterText, setCardFooterText] = useState<ReactNode>(() => {
    if (automationErrors?.gatherError?.message) {
      return automationErrors.gatherError.message
    }

    if (initialState?.message) {
      return initialState.message
    }

    if (recommendationState === RecommendationState.UPDATED) {
      return taskUpdatedText
    }

    return null
  })

  const updateButtonHelperText = useCallback(
    (text) => setButtonHelperText(text),
    [setButtonHelperText],
  )

  const handleTaskButtonClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      if (accessLevel === AccessLevel.PREVIEW) {
        onClickUpsell()
      } else {
        setTaskIsEnabled(true)
      }
    }, [accessLevel, onClickUpsell])

  const handleWarningButtonClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      onConfirmClick()
    }, [onConfirmClick])

  const handleWarningCancelButtonClick: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      setTaskIsEnabled(false)
    }, [])

  const timestampStr: string | undefined = useMemo(() => {
    if (
      recommendationState === RecommendationState.UPDATED &&
      lastUpdatedTimestamp &&
      lastUpdatedTimestamp > -1
    ) {
      return `${getTimeSinceTimestampStr(lastUpdatedTimestamp)} ago`
    }
    return undefined
  }, [lastUpdatedTimestamp, recommendationState])

  const handleUpdateFooterText = useCallback(
    (text) => setCardFooterText(text),
    [],
  )

  let taskButtonDisabled
  switch (recommendationKey) {
    case TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS:
      taskButtonDisabled =
        locked ||
        !initialState?.postInfo ||
        !initialState?.postInfo?.authorUsername
      break
    default:
      taskButtonDisabled = locked
  }

  let taskContent
  if (recommendationKey === TwitterRecommendationKey.TWITTER_BLOCK_REPOSTERS) {
    taskContent = (
      <SelectXPost
        defaultTaskTitle={taskTitle}
        hasError={Boolean(automationErrors?.message)}
        locked={locked}
        platformUserId={platformUserId}
        postAuthorName={initialState?.postInfo?.authorName}
        postAuthorUsername={initialState?.postInfo?.authorUsername}
        postContent={initialState?.postInfo?.content}
        postRepostCount={initialState?.postInfo?.repostCount}
      />
    )
  } else if (
    recommendationKey ===
      TwitterRecommendationKey.TWITTER_DELETE_POSTS_BEFORE ||
    recommendationKey ===
      BlueskyRecommendationKey.BLUESKY_DELETE_POSTS_BEFORE ||
    recommendationKey === LinkedinRecommendationKey.LINKEDIN_DELETE_POSTS_BEFORE
  ) {
    let minYear
    if (platform === PLATFORM.TWITTER) {
      minYear = 2006
    } else if (platform === PLATFORM.BLUESKY) {
      minYear = 2019
    } else {
      minYear = 2003 // Twitter founded in 2006, Bluesky in 2019, LinkedIn in 2003
    }
    taskContent = (
      <SelectDeleteTimeframe
        locked={locked}
        platform={platform}
        platformUserId={platformUserId}
        recommendationKey={recommendationKey}
        updateTaskButtonText={updateButtonHelperText}
        updateFooterText={handleUpdateFooterText}
        initialNumPosts={initialState?.numPosts}
        initialMostRecentPostDate={initialState?.mostRecentPostDate}
        initialCountDeleted={initialState?.count}
        settingsSelected={settingsSelected}
        settingKey={settingKey}
        selectedDateOption={settingsSelected?.[settingKey]?.dateOption}
        selectedMonth={settingsSelected?.[settingKey]?.sinceDateMonth}
        selectedYear={settingsSelected?.[settingKey]?.sinceDateYear}
        minMonth={0}
        minYear={minYear}
      />
    )
  } else if (
    recommendationKey === FBRecommendationKey.FB_DELETE_POSTS ||
    recommendationKey ===
      FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES ||
    recommendationKey === FBRecommendationKey.FB_DELETE_PAGE_LIKES
  ) {
    const settingsRows = settings.map((setting) => {
      // Setting selected default to true if no value in progress
      const isSettingSelected =
        settingsSelected[setting.key] !== null &&
        settingsSelected[setting.key] !== undefined
          ? settingsSelected[setting.key].isSelected
          : true

      const toggleSettingState = (sk) => {
        const updatedSettings = {
          ...settingsSelected,
          [sk]: {
            ...settingsSelected[sk],
            isSelected: !settingsSelected[sk]?.isSelected,
          },
        }

        state.updateSettingsSelected(
          platform,
          platformUserId,
          recommendationKey,
          updatedSettings,
        )
        return updatedSettings
      }

      return (
        <SettingRow
          currentValueDescription={null}
          key={setting.key}
          isLessRelevant={false}
          isSettingDisabled={!initialState}
          isSettingDisabledReason={null}
          isSettingFulfilled={false}
          isSettingNotUndoable
          isSettingSelected={isSettingSelected}
          label={setting.description}
          labelId={`${recommendationKey}-${setting.key}-label`}
          recommendationState={RecommendationState.INCOMPLETE} // Tasks are always INCOMPLETE
          onSelectSetting={
            locked ? () => {} : () => toggleSettingState(setting.key)
          }
          locked={locked}
        />
      )
    })

    let title
    switch (recommendationKey) {
      case FBRecommendationKey.FB_DELETE_POSTS:
        title = 'How old are the posts you want to delete?'
        break
      case FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES:
        title = 'How old are the interactions you want to delete?'
        break
      case FBRecommendationKey.FB_DELETE_PAGE_LIKES:
        title = 'How old are the page likes and interests you want to delete?'
        break
      default:
    }

    const minYear = initialState?.year ? parseInt(initialState?.year, 10) : 2004 // Facebook founded in 2004
    const minMonth = initialState?.month
      ? parseInt(initialState?.month, 10) - 1
      : 0 // 0-indexed month

    taskContent = (
      <div>
        {settingsRows}
        {initialState && (
          <SelectDeleteTimeframe
            locked={locked}
            platform={platform}
            platformUserId={platformUserId}
            recommendationKey={recommendationKey}
            updateTaskButtonText={updateButtonHelperText}
            updateFooterText={handleUpdateFooterText}
            initialNumPosts={initialState?.numPosts}
            initialMostRecentPostDate={initialState?.mostRecentPostDate}
            initialCountDeleted={initialState?.count}
            settingsSelected={settingsSelected}
            settingKey={settingKey}
            selectedDateOption={settingsSelected?.[settingKey]?.dateOption}
            selectedMonth={settingsSelected?.[settingKey]?.sinceDateMonth}
            selectedYear={settingsSelected?.[settingKey]?.sinceDateYear}
            minMonth={minMonth}
            minYear={minYear}
            title={title}
            subtitle={`Your oldest activity is from ${MONTHS[minMonth]} ${minYear}`}
          />
        )}
      </div>
    )
  } else if (taskTitle || (taskContext && taskContext.length > 0)) {
    taskContent = (
      <>
        <div className={styles['recommendation-body-title']}>
          <div className="body1 black bold">{taskTitle}</div>
        </div>
        <ul className={clsx('body2', 'black')}>
          {taskContext.map((ac) => (
            <li key={ac}>{ac}</li>
          ))}
        </ul>
      </>
    )
  }

  const requiresGather =
    !initialState &&
    (recommendationKey === FBRecommendationKey.FB_DELETE_POSTS ||
      recommendationKey ===
        FBRecommendationKey.FB_DELETE_COMMENTS_REACTIONS_VOTES ||
      recommendationKey === FBRecommendationKey.FB_DELETE_PAGE_LIKES)

  return (
    <div className={styles.taskBody}>
      {taskContent && (
        <div className={styles.recommendationBody}>{taskContent}</div>
      )}
      <div className={styles.recommendationFooter}>
        {taskIsEnabled && (
          <div
            className={clsx(
              styles['footer-description'],
              'body3',
              'darkest-gray',
            )}
          >
            {taskWarningText ||
              `This can't be undone. Are you sure you want to continue?`}
          </div>
        )}
        {!taskIsEnabled && cardFooterText && (
          <div
            className={clsx(
              styles['footer-description'],
              'body3',
              'darkest-gray',
            )}
          >
            <div>{cardFooterText}</div>
            <div>{timestampStr ? `${timestampStr}` : ''}</div>
          </div>
        )}
        <div className={styles['footer-buttons']}>
          {!taskIsEnabled && !requiresGather && (
            <TaskButton
              buttonText={
                recommendationState === RecommendationState.INCOMPLETE
                  ? taskButtonText
                  : continuingTaskButtonText
              }
              buttonHelperText={buttonHelperText}
              className={styles.taskButton}
              disabled={taskButtonDisabled}
              onClick={handleTaskButtonClick}
              preview={accessLevel === AccessLevel.PREVIEW}
            />
          )}
          {!taskIsEnabled && requiresGather && (
            <ActionButton
              onClick={onClickCheckSettings}
              disabled={locked}
              type={ActionButtonType.DANGER}
            >
              Check settings
            </ActionButton>
          )}
          {taskIsEnabled && (
            <>
              <WarningButton
                buttonText={confirmTaskButtonText}
                disabled={locked}
                onClick={handleWarningButtonClick}
              />
              <WarningCancelButton onClick={handleWarningCancelButtonClick} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
